import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { ChartBar, Users, Package, FileText, SignOut, Buildings, Receipt, ChartPieSlice, AddressBook, ChartLineUp, Scan, UserGear, Wallet, ChartDonut, Calculator, Target, UserPlus, IdentificationCard, BookOpen, Sparkle, Crosshair, ShoppingCart, Cube, X, Broadcast, EnvelopeOpen, Cpu, Trophy, WhatsappLogo, Truck } from "@phosphor-icons/react";
import Logo from "./Logo";
import { useAuth } from "../context/AuthContext";
import { useCompany } from "../context/CompanyContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const crmItems = [
  { to: "/dashboard/sales", label: "Sales Dashboard", icon: ChartLineUp, testId: "nav-dashboard-sales" },
  { to: "/leads", label: "Leads", icon: Users, testId: "nav-leads" },
  { to: "/leads/hunt", label: "AI Lead Hunt", icon: Crosshair, testId: "nav-lead-hunt" },
  { to: "/swarm", label: "AI Swarm Outreach", icon: Broadcast, testId: "nav-swarm" },
  { to: "/swarm/queue", label: "Autonomous Queue", icon: Broadcast, testId: "nav-swarm-queue" },
  { to: "/swarm/quora-inbox", label: "Quora Inbox", icon: BookOpen, testId: "nav-quora-inbox" },
  { to: "/email", label: "Email Center", icon: EnvelopeOpen, testId: "nav-email" },
  { to: "/whatsapp", label: "WhatsApp Center", icon: WhatsappLogo, testId: "nav-whatsapp" },
  { to: "/procurement", label: "Procurement Intel", icon: Cpu, testId: "nav-procurement" },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy, testId: "nav-leaderboard" },
  { to: "/reports/dsr", label: "Daily Sales Report", icon: ChartLineUp, testId: "nav-dsr" },
  { to: "/catalog", label: "Catalog", icon: Package, testId: "nav-catalog" },
  { to: "/proposals", label: "Proposals", icon: FileText, testId: "nav-proposals" },
];

const commandCenterItems = [
  { to: "/dashboard", label: "Command Center", icon: ChartBar, testId: "nav-dashboard" },
  { to: "/finance/snapshot", label: "Finance Snapshot", icon: ChartLineUp, testId: "nav-snapshot", adminOnly: true },
];

const financeItems = [
  { to: "/finance", label: "Overview", icon: ChartPieSlice, testId: "nav-finance" },
  { to: "/finance/group-health", label: "Group Health", icon: ChartDonut, testId: "nav-group-health" },
  { to: "/finance/work-orders", label: "Work Orders", icon: Receipt, testId: "nav-work-orders" },
  { to: "/finance/operating-expenses", label: "Operating Expenses", icon: Calculator, testId: "nav-operating-expenses" },
  { to: "/finance/purchases", label: "Purchases · COGS", icon: ShoppingCart, testId: "nav-purchases" },
  { to: "/dispatch", label: "Dispatch Pipeline", icon: Truck, testId: "nav-dispatch" },
  { to: "/finance/inventory", label: "Inventory", icon: Cube, testId: "nav-inventory" },
  { to: "/finance/vouchers", label: "Vouchers", icon: FileText, testId: "nav-vouchers" },
  { to: "/finance/voucher-ai", label: "Voucher AI → Busy", icon: FileText, testId: "nav-voucher-ai" },
  { to: "/finance/billing", label: "Billing · Invoices", icon: Receipt, testId: "nav-billing" },
  { to: "/finance/salaries", label: "Salaries", icon: Wallet, testId: "nav-salaries" },
  { to: "/finance/analysis", label: "AI Analysis", icon: ChartDonut, testId: "nav-analysis" },
  { to: "/finance/contacts", label: "Clients & Vendors", icon: AddressBook, testId: "nav-contacts" },
  { to: "/finance/companies", label: "Companies", icon: Buildings, testId: "nav-companies" },
  { to: "/finance/sync-settings", label: "Sync Settings", icon: UserGear, testId: "nav-sync-settings", adminOnly: true },
];

const hrItems = [
  { to: "/hr/onboarding", label: "Onboarding", icon: UserPlus, testId: "nav-hr-onboarding" },
  { to: "/hr/performance", label: "Performance Admin", icon: Target, testId: "nav-hr-performance" },
  { to: "/hr/letters", label: "HR Letters", icon: IdentificationCard, testId: "nav-hr-letters" },
  { to: "/hr/resumes", label: "Resume Repository", icon: Sparkle, testId: "nav-hr-resumes" },
];

const policiesItem = { to: "/hr/policies", label: "Policies", icon: BookOpen, testId: "nav-hr-policies" };

const misItems = [
  { to: "/mis/daily", label: "Daily MIS", icon: Scan, testId: "nav-mis-daily" },
  { to: "/mis/services", label: "Services & Rates", icon: Calculator, testId: "nav-mis-services" },
  { to: "/mis", label: "Monthly MIS", icon: ChartLineUp, testId: "nav-mis" },
  { to: "/mis/targets", label: "Targets & Optimizer", icon: Target, testId: "nav-mis-targets" },
  { to: "/mis/entities", label: "Billing Entities", icon: AddressBook, testId: "nav-mis-entities" },
];

const trackerItems = [
  { to: "/tracker", label: "Task Watchlist", icon: Target, testId: "nav-tracker" },
];

const adminItems = [
  { to: "/admin/users", label: "Users & Roles", icon: UserGear, testId: "nav-admin-users" },
];

function Section({ label, badge, children, testId }) {
  return (
    <div data-testid={testId}>
      <div className="px-3 mb-2 flex items-center gap-2">
        <span className="font-mono-shelv text-[10px] tracking-[0.2em] uppercase text-[#717680]">{label}</span>
        {badge && <span className="text-[9px] tracking-widest uppercase font-bold text-[#D90429]">{badge}</span>}
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function Item({ to, icon: Icon, label, testId, end, onClick }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testId}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 text-sm transition-all duration-200 ${
          isActive ? "bg-[#D90429] text-white font-semibold" : "text-[#0A0A0A] hover:bg-[#F5F6F8]"
        }`
      }
    >
      <Icon size={18} weight="bold" />
      {label}
    </NavLink>
  );
}

export default function Sidebar({ mobileOpen = false, onClose = () => {} }) {
  const { user, logout, hasRole, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { companies, activeId, setActive } = useCompany() || {};

  // Restricted MIS user = has 'mis' role but NOT admin and NOT any other functional role
  const roles = new Set(user?.roles || []);
  const isMisOnly = roles.has("mis") && !roles.has("admin")
    && !roles.has("crm") && !roles.has("finance") && !roles.has("hr") && !roles.has("tracker") && !roles.has("viewer");

  const showCrm = hasRole("crm", "viewer") && !isMisOnly;
  const showFinance = hasRole("finance", "viewer") && !isMisOnly;
  const showMis = hasRole("mis", "viewer");
  const showTracker = (hasRole("tracker", "viewer") || isAdmin) && !isMisOnly;
  const showHr = (hasRole("hr", "viewer") || isAdmin) && !isMisOnly;

  // For mis-only users, show ONLY the Daily MIS entry link
  const visibleMisItems = isMisOnly
    ? misItems.filter((it) => it.to === "/mis/daily")
    : misItems;

  // Auto-redirect mis-only users from dashboard/landing to their daily log
  useEffect(() => {
    if (isMisOnly && (location.pathname === "/dashboard" || location.pathname === "/")) {
      navigate("/mis/daily", { replace: true });
    }
    // eslint-disable-next-line
  }, [location.pathname, isMisOnly]);

  // Auto-close drawer when route changes (mobile)
  useEffect(() => { onClose(); /* eslint-disable-next-line */ }, [location.pathname]);

  // Lock body scroll when drawer open on mobile
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <>
      {/* Backdrop for mobile drawer */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm md:hidden"
          onClick={onClose}
          data-testid="sidebar-backdrop"
        />
      )}

      <aside
        className={`
          bg-white border-r border-black/10 flex flex-col
          fixed md:sticky top-0 left-0 z-50 md:z-auto
          h-screen w-72 md:w-64
          transform transition-transform duration-300 ease-in-out
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0
        `}
        data-testid="app-sidebar"
      >
        <div className="px-5 py-6 border-b border-black/10 flex items-center justify-between">
          <div
            className="cursor-pointer"
            onClick={() => { navigate(isMisOnly ? "/mis/daily" : "/dashboard"); onClose(); }}
          >
            <Logo size="sm" />
          </div>
          <button
            type="button"
            className="md:hidden p-1.5 -mr-1 text-[#0A0A0A] hover:bg-black/5"
            onClick={onClose}
            aria-label="Close menu"
            data-testid="sidebar-close-button"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-5">
          {!isMisOnly && (
            <Section label="Home" badge="ERP" testId="sidebar-home">
              {commandCenterItems.filter(it => !it.adminOnly || isAdmin).map((it) => (
                <Item key={it.to} {...it} end={it.to === "/dashboard"} onClick={onClose} />
              ))}
            </Section>
          )}

          {isAdmin && (
            <Section label="Admin" badge="Control" testId="sidebar-admin">
              {adminItems.map((it) => <Item key={it.to} {...it} onClick={onClose} />)}
            </Section>
          )}

          {showCrm && (
            <Section label="CRM" testId="sidebar-crm">
              {crmItems.map((it) => <Item key={it.to} {...it} end onClick={onClose} />)}
            </Section>
          )}

          {showFinance && (
            <Section label="Reyansh" badge="Finance" testId="sidebar-finance">
              {/* Company switcher */}
              {companies && companies.length > 0 && (
                <div className="px-3 mb-2">
                  <Select value={activeId || ""} onValueChange={(v) => setActive(v)}>
                    <SelectTrigger className="h-8 rounded-none text-xs" data-testid="company-switcher"><SelectValue placeholder="Select company…" /></SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => (<SelectItem key={c.id} value={c.id} data-testid={`company-option-${c.id}`}>{c.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {financeItems.filter(it => !it.adminOnly || isAdmin).map((it) => <Item key={it.to} {...it} end={it.to === "/finance"} onClick={onClose} />)}
            </Section>
          )}

          {showMis && (
            <Section label="MIS" badge="Productivity" testId="sidebar-mis">
              {visibleMisItems.map((it) => <Item key={it.to} {...it} end={it.to === "/mis"} onClick={onClose} />)}
            </Section>
          )}

          {showTracker && (
            <Section label="Shelves-Track" badge="Tasks" testId="sidebar-tracker">
              {trackerItems.map((it) => <Item key={it.to} {...it} end onClick={onClose} />)}
            </Section>
          )}

          {showHr && (
            <Section label="HR" badge="People" testId="sidebar-hr">
              {hrItems.map((it) => <Item key={it.to} {...it} end onClick={onClose} />)}
            </Section>
          )}

          {/* Policies visible to ALL authenticated users (read-only) — except restricted mis-only */}
          {!isMisOnly && (
            <Section label="Company" badge="Policies" testId="sidebar-policies">
              <Item key={policiesItem.to} {...policiesItem} end onClick={onClose} />
            </Section>
          )}
        </nav>

        <div className="border-t border-black/10 p-3">
          <div className="flex items-center gap-3 p-2 mb-2">
            {user?.picture ? (
              <img src={user.picture} alt={user?.name} className="w-8 h-8 rounded-full" />
            ) : (
              <div className="w-8 h-8 bg-[#D90429] text-white flex items-center justify-center text-xs font-bold">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-xs text-[#717680] truncate">{user?.email}</div>
            </div>
          </div>
          {user?.roles?.length > 0 && (
            <div className="flex flex-wrap gap-1 px-2 mb-2">
              {user.roles.map((r) => (
                <span key={r} className="text-[9px] font-mono-shelv uppercase tracking-widest bg-[#D90429]/10 text-[#D90429] px-1.5 py-0.5" data-testid={`role-badge-${r}`}>{r}</span>
              ))}
            </div>
          )}
          <button onClick={logout} data-testid="logout-button" className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#0A0A0A] hover:bg-[#F5F6F8] transition">
            <SignOut size={16} weight="bold" /> Logout
          </button>
        </div>
      </aside>
    </>
  );
}
